import { createSelector } from 'reselect';
import _isEmpty from 'lodash/isEmpty';

import { getActiveVenueId, getVenuesList } from 'helpers/active-venue';
import { IA_KEY, iaLogic } from 'helpers/ia-logic';
import {
  classDetail,
  msInventoryVenues,
  recommendations,
  userInfo,
  venueClasses,
  venueSmartspotData,
} from 'store/saga/entities/bound';
import {
  PERMISSION,
  DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
  DISMISSED_PRICING_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
  CP_EMAIL_REGEX,
  VENUE_SERVICE_TYPES,
} from 'constants/logic';
import { getKeyForUser } from 'helpers/local-storage';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import { parseISO } from 'date-fns';

// router

export const selectRouteParams = state => {
  const params = state.route.params || {};
  return Object.entries(params).reduce((acc, [k, v]) => {
    if (v === 'null') return acc;
    return { ...acc, [k]: v };
  }, {});
};

export const selectRouteParamStartDate = createSelector(
  selectRouteParams,
  routeParams => routeParams.startDate && parseISO(routeParams.startDate)
);

export const selectSchedulesStartDate = createSelector(
  state => state.app,
  selectRouteParamStartDate,
  (appState, routeParamsStartDate) => routeParamsStartDate || appState.schedulesStartDate
);
export const selectRouteQuery = state => {
  const route = state.route || {};
  const query = route.query || {};
  return Object.entries(query).reduce((acc, [k, v]) => {
    if (v === 'null') return acc;
    return { ...acc, [k]: v };
  }, {});
};

// user
export const selectUserIsLoaded = state => userInfo.loaded(state);
export const selectUserData = state => userInfo.data(state);
export const selectUserInfo = createSelector(selectUserIsLoaded, selectUserData, (isLoaded, user) =>
  isLoaded ? user : {}
);
export const selectUserIsAuthenticated = createSelector(selectUserInfo, user => user.isAuthenticated || false);
export const selectUserVenues = createSelector(selectUserInfo, user => getVenuesList(user));
export const selectUserId = createSelector(selectUserInfo, user => user.userId);
export const selectUserVenuesAsMap = createSelector(selectUserVenues, venues =>
  venues.reduce((memo, venue) => {
    memo[venue.venueId] = venue;
    return memo;
  }, {})
);
export const selectUserVenueIds = createSelector(selectUserVenues, venues => venues.map(({ venueId }) => venueId));
export const selectUserHasIntegratedVenue = createSelector(selectUserVenues, venues =>
  venues.some(({ isManualStudio }) => !isManualStudio)
);
export const selectActiveIntegratedVenue = createSelector(selectUserVenues, venues =>
  venues.find(({ isManualStudio }) => isManualStudio)
);
export const selectUserPermissions = createSelector(selectUserInfo, user => user.permissions || []);
// TODO: everyone should use this
export const selectUserHasPermission = createSelector(
  (state, permission = '') => permission,
  selectUserPermissions,
  (permission, userPermissions) => userPermissions.includes(permission)
);
export const selectUserHasPermissions = createSelector(
  (state, permissions = []) => permissions,
  selectUserPermissions,
  (permissions, userPermissions) => permissions.every(val => userPermissions.includes(val))
);

// schedule
export const selectActiveScheduleId = createSelector(selectRouteParams, routeParams => routeParams.scheduleId || null);

// class
export const selectActiveClassId = createSelector(selectRouteParams, routeParams => routeParams.classId || null);
export const selectClassDetailsIsLoading = (state, classId) => classDetail.loading(state, classId);

export const selectClassDetailsIsLoaded = (state, classId) => classDetail.loaded(state, classId);
const selectClassDetailsData = (state, classId) => classDetail.data(state, classId);
export const selectClassDetails = createSelector(selectClassDetailsIsLoaded, selectClassDetailsData, (isLoaded, data) =>
  isLoaded ? data : {}
);

// venue
export const selectActiveVenueId = createSelector(
  selectUserInfo,
  selectRouteParams,
  selectRouteQuery,
  (user, routeParams, routeQuery) => getActiveVenueId(user, routeParams, routeQuery)
);
export const selectActiveVenue = createSelector(
  selectUserVenues,
  selectActiveVenueId,
  (venues, activeVenueId) => venues.find(({ venueId }) => venueId === activeVenueId) || {}
);
export const selectActiveVenueTimezone = createSelector(selectActiveVenue, venue => venue.timezone);
export const selectVenueIdRouteParam = createSelector(selectRouteParams, routeParams => routeParams.venueId || null);
export const selectVenueByVenueId = createSelector(
  (state, venueId = '') => venueId,
  selectUserVenuesAsMap,
  (venueId, venuesMap) => venuesMap[venueId] || {}
);
const selectVenueClassesVenueId = createSelector(
  state => state || {},
  (state, venueId) => venueId,
  selectActiveVenueId,
  (state, passedVenueId, activeVenueId) => passedVenueId || activeVenueId
);
export const selectVenueClassesLoading = createSelector(
  state => state || {},
  selectVenueClassesVenueId,
  (state, venueId) => venueClasses.loading(state, venueId)
);
export const selectVenueClassesLoaded = createSelector(
  state => state || {},
  selectVenueClassesVenueId,
  (state, venueId) => venueClasses.loaded(state, venueId)
);
export const selectVenueClasses = createSelector(
  state => state || {},
  selectVenueClassesVenueId,
  selectVenueClassesLoaded,
  (state, venueId, isLoaded) => (isLoaded ? venueClasses.data(state, venueId) : [])
);

export const selectIaLogic = createSelector(
  (_, iaKey, venueId) => ({ iaKey, venueId }),
  selectUserInfo,
  selectActiveVenueId,
  ({ iaKey, venueId }, user, activeVenueId) => {
    const { userHasAccess, featureIsSupported } = iaLogic;
    return {
      userHasAccess: userHasAccess(iaKey)(user),
      featureIsSupported: featureIsSupported(iaKey)(user, venueId || activeVenueId),
    };
  }
);

// smartspot selectors with explicit venueId parameter
const selectVenueSmartspotDataByVenueId = createSelector(
  state => state || {},
  (_, venueId) => venueId,
  (state, venueId) => venueSmartspotData.data(state, venueId)
);
export const selectVenueSmartspotDataIsLoadingByVenueId = createSelector(
  state => state || {},
  (_, venueId) => venueId,
  (state, venueId) => venueSmartspotData.loading(state, venueId)
);
export const selectVenueSmartspotDataIsLoadedByVenueId = createSelector(
  state => state || {},
  (_, venueId) => venueId,
  selectVenueSmartspotDataByVenueId,
  (state, venueId, smartspotData) => !_isEmpty(smartspotData) || venueSmartspotData.loaded(state, venueId)
);
export const selectVenueSmartspotEnabledByVenueId = createSelector(
  selectVenueSmartspotDataByVenueId,
  smartspotData => smartspotData?.isEnabled
);

// smartspot selectors with active venue ID
export const selectVenueSmartspotDataRaw = createSelector(
  state => state || {},
  selectActiveVenueId,
  (state, venueId) => venueSmartspotData.data(state, venueId)
);
export const selectVenueSmartspotDataIsLoaded = createSelector(
  state => state || {},
  selectActiveVenueId,
  selectVenueSmartspotDataRaw,
  (state, venueId, smartspotData) => !_isEmpty(smartspotData) || venueSmartspotData.loaded(state, venueId)
);
export const selectVenueSmartspotDataIsLoading = createSelector(
  state => state || {},
  selectActiveVenueId,
  (state, venueId) => venueSmartspotData.loading(state, venueId)
);
export const selectVenueSmartspotData = createSelector(
  selectVenueSmartspotDataIsLoaded,
  selectVenueSmartspotDataRaw,
  (isLoaded, smartspotData) => (isLoaded ? smartspotData : { isEnabled: false, scheduleData: {} })
);
export const selectVenueSmartspotEnabled = createSelector(
  selectVenueSmartspotData,
  smartspotData => smartspotData.isEnabled
);
export const selectVenueSmartspotCanTurnOnOff = createSelector(
  selectActiveVenue,
  state => selectIaLogic(state, IA_KEY.SMART_SPOT),
  ({ isSmartspotEligible, smartspotCannotManageEnabled, isManualStudio }, { userHasAccess: canManageSmartSpot }) =>
    isSmartspotEligible && !isManualStudio && canManageSmartSpot && !smartspotCannotManageEnabled
);
export const selectClassSmartspotCanTurnOnOff = createSelector(
  selectVenueSmartspotEnabled,
  state => selectIaLogic(state, IA_KEY.SMART_SPOT),
  state => selectUserHasPermission(state, PERMISSION.DISPLAY_SPOT_MANAGEMENT),
  state => selectUserHasPermission(state, PERMISSION.CAN_SCHEDULE_SMARTSPOT_OFF),
  state => selectUserHasPermission(state, PERMISSION.CAN_SCHEDULE_SMARTSPOT_OFF_NR),
  (
    venueSmartspotEnabled,
    { userHasAccess: canManageSmartSpot },
    canDisplaySpotManagement,
    canScheduleSmartspotOff,
    canScheduleSmartspotOffNonRecurring
  ) =>
    venueSmartspotEnabled &&
    canManageSmartSpot &&
    canDisplaySpotManagement &&
    (canScheduleSmartspotOff || canScheduleSmartspotOffNonRecurring)
);
export const selectVenueName = createSelector(selectActiveVenue, venue => venue.venueName);
export const selectVenueTimezone = createSelector(selectActiveVenue, venue => venue.timezone);
export const selectCurrentTimeAtVenue = createSelector(
  selectVenueTimezone,
  (state, timestamp) => timestamp || new Date().getTime(), // deliberate UTC parse to make selector cacheable
  (venueTimezome, timestamp) => adjustDateForTimezone(new Date(timestamp), venueTimezome)
);

// Don't use createSelector here because we dont want this selector to be cached
export const selectDismissedSmartSpotBannerVenueIds = state => {
  const { userId } = selectUserInfo(state);
  return getKeyForUser(userId, DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY, []);
};

// Not cached: reads localStorage, which createSelector can't see changes to.
export const selectDismissedPricingBannerVenueIds = state => {
  const { userId } = selectUserInfo(state);
  return getKeyForUser(userId, DISMISSED_PRICING_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY, []);
};

export const selectShowSmartSpotBanner = createSelector(
  selectVenueSmartspotDataIsLoaded,
  selectActiveVenue,
  selectDismissedSmartSpotBannerVenueIds,
  selectVenueSmartspotEnabled,
  state => selectUserHasPermission(state, PERMISSION.CAN_MANAGE_SMARTSPOT),
  (loaded, activeVenue, dismissedVenueIds, smartspotEnabled, canManageSmartSpot) => {
    return (
      activeVenue.isSmartspotEligible &&
      loaded &&
      !smartspotEnabled &&
      canManageSmartSpot &&
      !dismissedVenueIds.includes(activeVenue.venueId)
    );
  }
);

export const selectMsInventoryVenues = createSelector(
  state => state || {},
  selectActiveVenueId,
  (state, venueId) => (msInventoryVenues.loaded(state, venueId) ? msInventoryVenues.data(state, venueId) : [])
);

export const selectActiveMsInventoryVenue = createSelector(
  selectMsInventoryVenues,
  selectUserVenues,
  (venues, allVenues) => venues.find(venue => venue.current) || allVenues.find(venue => venue.isMsInventoryVenue) || {}
);

export const selectUserHasCPEmail = createSelector(selectUserInfo, ({ email }) => CP_EMAIL_REGEX.test(email));

export const selectRecommendations = (state, key) =>
  recommendations.data(state, recommendations.defaultKey(null, key))?.recommendations;

export const selectIsAppointmentTypeVenue = createSelector(selectActiveVenue, activeVenue => {
  const { serviceTypes } = activeVenue;
  return serviceTypes.includes(VENUE_SERVICE_TYPES.APPOINTMENT);
});
